<template>
  <div :class="[$setClasses, isBox ? 'u-is-box --is-box' : '--is-inline']">
    <div v-if="hasPartnerDiscount"
         :class="$vClass('discount')">
      <partner-discount-banner :rate="partnerDiscountRate"
                               :label-key="isBox ? 'resume.partnerDiscount.banner' : 'base.partnerDiscountBanner'"
                               :class="$vClass('discount__banner')" />
    </div>
    <div v-if="$slots?.price"
        :class="$vClass('wrapper__price')">
      <slot name="price"
            :formatted-discount-amount="formattedDiscountAmount"
            :formatted-original-price="formattedOriginalPrice"
            :formatted-price="formattedPrice"
            :has-partner-discount="hasPartnerDiscount"></slot>
    </div>
    <template v-else>
      <p v-html="content"></p>
      <p v-if="hasPartnerDiscount"
         :class="$vClass('discount__before-price')">
        {{ formattedOriginalPrice }}
      </p>
      <p v-if="hasPartnerDiscount"
         :class="$vClass('discount__saving')">
        {{ $t('resume.partnerDiscount.saving', { price: formattedDiscountAmount }) }}
      </p>
    </template>
    <content-warning v-if="warningPrice"
                     :class="$vClass('warning')"
                     :icon="[]">
      {{ $t('resume.preBooking.remainingCost') }}
      : <price-diff :demand="demand"
                    :offer="offer"></price-diff>
    </content-warning>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import offersHelper from '@/utils/helpers/offers';
import ContentWarning from './ContentWarning.vue';
import PriceDiff from './PriceDiff.vue';
import PartnerDiscountBanner from '@/components/offer/fragments/PartnerDiscountBanner.vue';
import { useDemandStore, useProjectStore } from '@/stores';

export default {
  name: 'total',
  nameClassStyle: 'total',
  components: { ContentWarning, PartnerDiscountBanner, PriceDiff },
  props: {
    isBox: Boolean,
    offer: Object,
    warningPrice: Boolean,
    displayPriceType: {
      type: Boolean,
      default: true,
    },
    originalPrice: [String, Number],
    partnerDiscountRate: {
      type: Number,
      default: 0,
    },
    showPartnerDiscount: Boolean,
    price: [String, Number],
  },
  computed: {
    ...mapState(useDemandStore, ['demand']),
    ...mapState(useProjectStore, ['projectSelected']),
    setPrice() {
      return this.price || ((this.offer
        && offersHelper.getPrice(this.offer, this.demand.show_price_ht)) || '');
    },
    formattedPrice() {
      return this.formatPrice(this.setPrice, this.demand.show_price_ht, this.demand.priceTypeKey);
    },
    hasPartnerDiscount() {
      return this.showPartnerDiscount
        && Number(this.originalPrice || 0) > Number(this.setPrice || 0);
    },
    formattedOriginalPrice() {
      return this.hasPartnerDiscount
        ? this.formatPrice(this.originalPrice, this.demand.show_price_ht, this.demand.priceTypeKey)
        : '';
    },
    discountAmount() {
      if (this.hasPartnerDiscount) {
        return Math.round((Number(this.originalPrice) - Number(this.setPrice)) * 100) / 100;
      }

      return offersHelper.getPartnerDiscountAmount(this.offer, this.demand.show_price_ht);
    },
    formattedDiscountAmount() {
      return this.formatPrice(
        this.discountAmount,
        this.demand.show_price_ht,
        this.demand.priceTypeKey,
      );
    },
    content() {
      return this.$t('resume.total', { price: this.formattedPrice });
    },
  },
  methods: {
    formatPrice(price, priceType, priceTypeKey) {
      priceType = (this.displayPriceType !== false && ` ${this.$t('base.priceType', priceTypeKey)}`) || '';
      return this.$n(price || 0, 'currency') + priceType;
    },
  },
};
</script>

<style scoped lang="scss">
.total {
  &.--is-inline {
    text-align: end;
    margin-right: 20px;
    :deep(.total__discount) {
      margin-bottom: 12px;
    }
    :deep(p) b {
      margin-left: 20px;
    }
  }

  &.--is-box {
    text-align: center;

    :deep(.total__price) {
      margin: 0;
      font-size: 30px;
      font-weight: 700;
    }

    :deep(.total__discount) {
      margin: -20px -20px 20px;
    }
  }

  &__discount {
    display: flex;
    flex-direction: column;
    align-items: inherit;
    gap: 8px;
  }

  &.--is-box :deep(.total__discount__banner) {
    align-self: stretch;
    width: auto;
    max-width: 100%;
    box-sizing: border-box;
    border-radius: 0 0 7px 7px;
    font-size: 11px;
  }

  &.--is-box :deep(.total__wrapper__price) {
    margin-top: 12px;
  }

  &__discount__before-price {
    margin: 0;
    color: var(--nxt-color-grey);
    font-size: 18px;
    font-weight: 600;
    text-decoration: line-through;
  }

  &__discount__saving {
    margin: 0;
    color: var(--nxt-color-warning);
    font-size: 14px;
    font-weight: 700;
  }
}
</style>
